'use client';

import { useState, useMemo } from 'react';
import type { Item, Relationship, CardType, CardRarity, RelicRarity } from '@/lib/types';
import ItemCard from './ItemCard';

type ItemTypeFilter = 'all' | 'card' | 'relic';
type CardTypeFilter = 'all' | CardType;
type RarityFilter = 'all' | CardRarity | RelicRarity;

const CARD_TYPE_FILTERS: { label: string; value: CardTypeFilter }[] = [
  { label: 'All types', value: 'all' },
  { label: 'Attack', value: 'attack' },
  { label: 'Skill', value: 'skill' },
  { label: 'Power', value: 'power' },
  { label: 'Curse', value: 'curse' },
];

const RARITY_FILTERS: { label: string; value: RarityFilter }[] = [
  { label: 'All rarities', value: 'all' },
  { label: 'Basic', value: 'basic' },
  { label: 'Common', value: 'common' },
  { label: 'Uncommon', value: 'uncommon' },
  { label: 'Rare', value: 'rare' },
];

interface Props {
  items: Item[];
  relationships: Relationship[];
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${
        active
          ? 'bg-violet-600 text-white'
          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
      }`}
    >
      {children}
    </button>
  );
}

export default function Browser({ items, relationships }: Props) {
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<ItemTypeFilter>('all');
  const [cardTypeFilter, setCardTypeFilter] = useState<CardTypeFilter>('all');
  const [rarityFilter, setRarityFilter] = useState<RarityFilter>('all');

  const relationshipCountById = useMemo(() => {
    const counts = new Map<string, number>();
    for (const rel of relationships) {
      counts.set(rel.sourceId, (counts.get(rel.sourceId) ?? 0) + 1);
      counts.set(rel.targetId, (counts.get(rel.targetId) ?? 0) + 1);
    }
    return counts;
  }, [relationships]);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (typeFilter !== 'all' && item.itemType !== typeFilter) return false;
      if (item.itemType === 'card' && cardTypeFilter !== 'all' && item.type !== cardTypeFilter) return false;
      if (rarityFilter !== 'all' && item.rarity !== rarityFilter) return false;
      if (query.trim()) {
        const q = query.trim().toLowerCase();
        if (!item.name.toLowerCase().includes(q) && !item.description.toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [items, query, typeFilter, cardTypeFilter, rarityFilter]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Search + filters */}
        <div className="px-6 py-3 border-b border-slate-800 space-y-3 max-w-7xl w-full mx-auto">
          <input
            type="text"
            placeholder="Search cards and relics…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
          />

          <div className="flex flex-wrap gap-1.5">
            {(['all', 'card', 'relic'] as const).map((t) => (
              <FilterPill key={t} active={typeFilter === t} onClick={() => setTypeFilter(t)}>
                {t === 'all' ? 'All' : t === 'card' ? 'Cards' : 'Relics'}
              </FilterPill>
            ))}
            <span className="w-px bg-slate-700 mx-1" />
            {CARD_TYPE_FILTERS.map(({ label, value }) => (
              <FilterPill
                key={value}
                active={cardTypeFilter === value}
                onClick={() => setCardTypeFilter(value)}
              >
                {label}
              </FilterPill>
            ))}
            <span className="w-px bg-slate-700 mx-1" />
            {RARITY_FILTERS.map(({ label, value }) => (
              <FilterPill
                key={value}
                active={rarityFilter === value}
                onClick={() => setRarityFilter(value)}
              >
                {label}
              </FilterPill>
            ))}
          </div>

          <p className="text-xs text-slate-500">
            {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
          </p>
        </div>

        {/* Item grid */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="max-w-7xl mx-auto">
            {filtered.length === 0 ? (
              <p className="text-sm text-slate-500 text-center mt-8">No results found.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {filtered.map((item) => (
                  <ItemCard
                    key={item.id}
                    item={item}
                    relationshipCount={relationshipCountById.get(item.id) ?? 0}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
